import React, { useState } from 'react';
import Modal from './ui/Modal';
import { createPlaylist } from '../lib/api';

interface CreatePlaylistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPlaylistCreated: () => void;
}

const CreatePlaylistModal: React.FC<CreatePlaylistModalProps> = ({
  isOpen,
  onClose,
  onPlaylistCreated
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setLoading(true);
    setError(null);

    try {
      await createPlaylist(name.trim(), description.trim() || undefined, isPublic);
      onPlaylistCreated();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create playlist');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setIsPublic(false);
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create Playlist">
      <form onSubmit={handleSubmit}>
        {error && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-xl text-red-200 text-sm">
            {error}
          </div>
        )}

        <div className="mb-4">
          <label htmlFor="playlist-name" className="block mb-2 text-sm font-medium text-gray-300">
            Playlist Name
          </label>
          <input
            id="playlist-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-gray-900 border border-gray-700 text-white text-sm rounded-xl w-full p-3 focus:outline-none focus:ring-2 focus:ring-[#FF4D8C] focus:border-transparent transition-all duration-200"
            placeholder="My Awesome Playlist"
            required
            maxLength={100}
          />
        </div>

        <div className="mb-4">
          <label htmlFor="playlist-description" className="block mb-2 text-sm font-medium text-gray-300">
            Description (optional)
          </label>
          <textarea
            id="playlist-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="bg-gray-900 border border-gray-700 text-white text-sm rounded-xl w-full p-3 focus:outline-none focus:ring-2 focus:ring-[#FF4D8C] focus:border-transparent transition-all duration-200 resize-none"
            placeholder="What's this playlist about?"
            rows={3}
            maxLength={500}
          />
        </div>

        <div className="mb-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="sr-only"
            />
            <div className={`relative w-11 h-6 rounded-full transition-colors ${isPublic ? 'bg-[#FF4D8C]' : 'bg-gray-700'}`}>
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${isPublic ? 'translate-x-5' : 'translate-x-0'}`} />
            </div>
            <span className="ml-3 text-sm text-gray-300">Make playlist public</span>
          </label>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border border-gray-700 text-gray-300 rounded-xl hover:bg-gray-800 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !name.trim()}
            className="px-6 py-2 bg-[#FF4D8C] hover:bg-[#FF6E9E] text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </span>
            ) : (
              'Create Playlist'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreatePlaylistModal;